import { CLASS_LIMITS, type ClassDTO, type ClassGoalDTO, type FriendNotebookResponse, type FriendsResponse, type SharedShelfDTO } from '@shared/protocol/messages';
import { heroClass } from '../../data/presets';
import { CLASS_GOAL_COINS, claimClassPrize, classPrizeReady, goalLeft, tapeShared } from '../../systems/classroom';
import { artImg, button, confirmBox, formatDate, meter, modal, statChip, stickyNote, thumb } from '../../../ui/components';
import { h, setChildren } from '../../../ui/dom';
import { doodleSvg } from '../../../ui/doodleSvg';
import { keepClass, publishNotebookCard } from '../../../networking/classroom';
import { ensureOnline } from '../../../networking/session';
import { language, t, tn } from '../../../i18n';
import { card, errorText } from './community';
import type { NotebookCtx } from './tabs';

/** ABC123 → ABC-123, easier to read out loud in class. */
export const formatClassCode = (code: string): string => (code.length === 6 ? `${code.slice(0, 3)}-${code.slice(3)}` : code);

/** Most monsters on the class shelf (newest first). */
const SHELF_MAX = 24;

function weekEnds(iso: string): string {
  return new Date(iso).toLocaleString(language() === 'th' ? 'th-TH' : undefined, { weekday: 'long', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
}

const hint = (text: string): HTMLElement => h('p', { class: 'hand-hint' }, text);

/** Tapes a shared monster into my notebook; returns its id, or null when it no longer fits the rules. */
function tapeIn(ctx: NotebookCtx, item: SharedShelfDTO, author: string): string | null {
  let id: string | null = null;
  ctx.app.store.update(['monsters'], (s) => (id = tapeShared(s, item, author)?.id ?? null));
  if (!id) ctx.app.toast(t('That monster does not fit the rules any more.'), 'error');
  return id;
}

const haveShared = (ctx: NotebookCtx, code: string): boolean => ctx.app.store.state.monsters.some((m) => m.source === 'shared' && m.shareCode === code);

/* ------------------------------ Friend's notebook ------------------------ */

/** A friend's notebook page, read-only: hero, collection, Hall of Fame wins and shared monsters. */
export function friendNotebookPage(ctx: NotebookCtx, nb: FriendNotebookResponse): HTMLElement {
  const { app } = ctx;
  const c = nb.card;
  const shelf = nb.shared.map((item) => {
    const action = h('div', { class: 'card-actions' });
    const fill = (): void => {
      setChildren(
        action,
        haveShared(ctx, item.code)
          ? h('span', { class: 'stamp green' }, t('IN MY NOTEBOOK'))
          : button(t('Tape into my notebook'), {
              small: true,
              color: 'blue',
              sfx: app,
              onClick: () => {
                if (!tapeIn(ctx, item, nb.name)) return;
                app.toast(t('{name} was taped into your notebook!', { name: item.monster.name }), 'success');
                fill();
              },
            }),
      );
    };
    fill();
    return card(ctx, item.monster, t('Shared {date}', { date: formatDate(Date.parse(item.createdAt)) }), 'shelf-card', action);
  });
  return h(
    'div',
    { class: 'friend-notebook' },
    h(
      'div',
      { class: 'friend-hero' },
      c?.heroClass ? thumb(app, null, `hero:${c.heroClass}`, 96) : artImg(app, 'sticker:pencil', 'sticker', doodleSvg('pencil', 'sticker')),
      h(
        'div',
        null,
        h('h3', null, nb.name),
        h(
          'p',
          { class: 'card-sub' },
          c?.heroClass ? t('Playing a level {level} {cls}', { level: c.heroLevel, cls: t(heroClass(c.heroClass).name) }) : c ? t('No hero yet.') : t('{name} has not opened their notebook online yet.', { name: nb.name }),
        ),
        h(
          'div',
          { class: 'chips' },
          statChip('🏆', nb.trophies),
          statChip(t('Victory Stamps'), c?.stamps ?? 0),
          statChip(t('Stationery'), c?.cosmetics ?? 0),
          statChip(t('Contest wins'), nb.hallWins.length),
        ),
        nb.updatedAt ? h('p', { class: 'hand-hint' }, t('Last updated {date}', { date: formatDate(Date.parse(nb.updatedAt)) })) : null,
      ),
    ),
    h('h3', { class: 'section-title' }, t('Hall of Fame')),
    nb.hallWins.length
      ? h(
          'ul',
          { class: 'hall-wins' },
          nb.hallWins.map((w) => h('li', null, h('span', { class: 'stamp gold' }, t('CHAMPION')), ` ${t('{monster} won {round}', { monster: w.monsterName, round: w.round })}`)),
        )
      : hint(t('No contest wins yet.')),
    h('h3', { class: 'section-title' }, t('Shared Monsters')),
    shelf.length ? h('div', { class: 'card-grid' }, shelf) : hint(t('{name} has not shared a monster yet.', { name: nb.name })),
  );
}

/** Opens a friend's notebook (friends only) in a read-only pop-up. */
export async function openFriendNotebook(ctx: NotebookCtx, friend: { id: string; name: string }): Promise<void> {
  const { app } = ctx;
  let nb: FriendNotebookResponse;
  try {
    nb = await app.api.friendNotebook(friend.id);
  } catch (err) {
    app.toast(errorText(err), 'error');
    return;
  }
  app.sfx.play('flip');
  const shown = modal(t("{name}'s Notebook", { name: nb.name }), friendNotebookPage(ctx, nb), [{ label: t('Close'), value: 'ok', variant: 'primary', color: 'yellow' }], {
    className: 'friend-notebook-modal',
  });
  // The pop-up focuses its first button, which can scroll the page title away: start at the top.
  document.querySelector('.friend-notebook-modal')?.scrollTo({ top: 0 });
  await shown;
}

/* ------------------------------ Class goal ------------------------------- */

function goalNote(ctx: NotebookCtx, cls: ClassDTO, reload: () => void): HTMLElement {
  const { app } = ctx;
  const g: ClassGoalDTO = cls.goal;
  const claimed = app.store.state.community.classPrizes.includes(g.round);
  return stickyNote(
    g.done ? 'green' : 'pink',
    h('p', null, h('b', null, t("This Week's Class Goal"))),
    h('p', null, t('Every battle counts, win or lose! Fight {target} battles together this week.', { target: g.target })),
    meter(g.progress, g.target, 'exp', `${g.progress} / ${g.target}`),
    h(
      'p',
      null,
      g.done ? t('Goal reached! Great teamwork.') : tn(goalLeft(g), '{n} battle to go.', '{n} battles to go.'),
      ' ',
      t('My battles: {n} (up to {cap} count).', { n: g.myBattles, cap: g.battleCap }),
    ),
    classPrizeReady(app.store.state, g)
      ? button(h('span', null, `${t('Claim!')} +`, h('span', { class: 'coin', 'aria-hidden': 'true' }), ` ${CLASS_GOAL_COINS}`), {
          variant: 'primary',
          color: 'yellow',
          sfx: app,
          onClick: () => {
            let coins = 0;
            app.store.update(['inventory', 'memories'], (s) => (coins = claimClassPrize(s, g, cls.name)));
            if (coins) {
              app.sfx.play('win');
              app.toast(t('+{n} coins for the class goal!', { n: coins }), 'success');
            }
            reload();
          },
        })
      : claimed
        ? h('span', { class: 'stamp green' }, t('CLAIMED'))
        : g.done
          ? hint(t('Fight one battle this week to share the prize.'))
          : hint(t('Reach the goal and everyone who helped gets +{n} coins.', { n: CLASS_GOAL_COINS })),
    hint(t('New goal {when}.', { when: weekEnds(g.endsAt) })),
  );
}

/* ------------------------------ Class page ------------------------------- */

type Link = 'friend' | 'pending' | null;

function linkOf(friends: FriendsResponse | null, id: string): Link {
  if (!friends) return null;
  if (friends.friends.some((f) => f.id === id)) return 'friend';
  return [...friends.incoming, ...friends.outgoing].some((f) => f.id === id) ? 'pending' : null;
}

/** The class board: code, members, weekly goal and everyone's shared monsters. */
export function classTab(ctx: NotebookCtx): HTMLElement {
  const { app } = ctx;
  const box = h('div', { class: 'class-board' }, hint(t('Opening the class register…')));
  const page = h(
    'div',
    { class: 'tab-class' },
    h('header', { class: 'page-header' }, h('h2', { class: 'page-heading' }, t('My Class')), h('p', { class: 'page-sub' }, t('One class code, the whole class in one notebook.'))),
    h('div', { class: 'row-actions' }, button(t('← Friends'), { small: true, variant: 'ghost', sfx: app, onClick: () => ctx.goTab('friends') })),
    box,
  );
  if (!app.store.state.online) {
    setChildren(
      box,
      hint(t('Sign in to the battle server to join a class.')),
      button(t('Sign in'), { variant: 'primary', color: 'yellow', sfx: app, onClick: () => void ensureOnline(app).then((ok) => ok && ctx.refresh()) }),
    );
    return page;
  }
  let alive = true;
  ctx.onCleanup(() => (alive = false));
  // Kept across re-renders so a reload never wipes a half-typed code.
  const codeInput = h('input', { class: 'hand-input code class-code-input', maxLength: 7, placeholder: 'ABC-123', 'aria-label': t('Class code'), autocomplete: 'off' });

  const act = async (fn: () => Promise<string | void>): Promise<void> => {
    try {
      const msg = await fn();
      if (msg) app.toast(msg, 'success');
    } catch (err) {
      app.toast(errorText(err), 'error');
    }
    await load();
  };
  const join = (): void => {
    const code = codeInput.value.replace(/[\s-]/g, '').toUpperCase();
    if (code.length !== 6) return app.toast(t('Class codes have 6 letters/numbers, like ABC-123.'), 'info');
    void act(async () => {
      const cls = await app.api.joinClass(code);
      keepClass(app, cls);
      codeInput.value = '';
      app.sfx.play('pop');
      return t('Welcome to {name}!', { name: cls.name });
    });
  };
  codeInput.addEventListener('keydown', (e) => e.key === 'Enter' && join());

  const renderNone = (): void => {
    setChildren(
      box,
      stickyNote(
        'yellow',
        h('p', null, h('b', null, t('Join a class'))),
        h('p', null, t('Ask your teacher or a classmate for the class code.')),
        h('div', { class: 'import-row' }, codeInput, button(t('Join'), { small: true, color: 'blue', sfx: app, onClick: join })),
      ),
      stickyNote(
        'blue',
        h('p', null, h('b', null, t('Start a class'))),
        h('p', null, t('The game picks a friendly class name and gives you a code to share. Up to {n} classmates.', { n: CLASS_LIMITS.maxMembers })),
        button(t('Start a class'), {
          variant: 'primary',
          color: 'yellow',
          sfx: app,
          onClick: () =>
            void act(async () => {
              const cls = await app.api.createClass(language());
              keepClass(app, cls);
              return t('{name} is ready! Share the code {code}.', { name: cls.name, code: formatClassCode(cls.code) });
            }),
        }),
      ),
    );
  };

  const render = (cls: ClassDTO, friends: FriendsResponse | null): void => {
    const me = app.store.state.online?.playerId;
    const owner = cls.members.find((m) => m.owner);
    const members = cls.members.map((m) => {
      const link = linkOf(friends, m.id);
      const actions: HTMLElement[] = [];
      if (m.id !== me) {
        if (link === 'friend') actions.push(button(t('Notebook'), { small: true, color: 'blue', sfx: app, onClick: () => void openFriendNotebook(ctx, m) }));
        else if (link === 'pending') actions.push(h('span', { class: 'friend-meta' }, t('friend request waiting')));
        else
          actions.push(
            button(t('Add friend'), {
              small: true,
              color: 'green',
              sfx: app,
              onClick: () =>
                void act(async () => {
                  const f = await app.api.addFriend({ playerId: m.id });
                  return f.status === 'accepted' ? t('You and {name} are now friends!', { name: f.name }) : t('Friend request sent to {name}.', { name: f.name });
                }),
            }),
          );
        if (cls.mine)
          actions.push(
            button(t('Take out'), {
              small: true,
              variant: 'ghost',
              sfx: app,
              onClick: () =>
                void confirmBox(t('Take {name} out of the class?', { name: m.name }), t('{name} will not be able to join this class again.', { name: m.name }), t('Take out')).then((yes) => {
                  if (yes) void act(async () => (await app.api.kickClassMember(m.id), t('{name} left the class.', { name: m.name })));
                }),
            }),
          );
      }
      return h(
        'li',
        { class: `friend class-member ${m.id === me ? 'me' : ''}`, 'data-member': m.id },
        artImg(app, `sticker:${m.owner ? 'crown' : 'star'}`, 'sticker', doodleSvg(m.owner ? 'crown' : 'star', 'sticker')),
        h('b', null, m.name),
        h(
          'span',
          { class: 'friend-meta' },
          ` ${m.trophies} 🏆 · ${tn(m.weekBattles, '{n} battle this week', '{n} battles this week')}${m.owner ? ` · ${t('class owner')}` : ''}${m.id === me ? ` · ${t('you')}` : ''}`,
        ),
        h('span', { class: 'friend-actions' }, actions),
      );
    });

    const shelf = cls.members
      .flatMap((m) => m.shared.map((item) => ({ item, author: m.name, mine: m.id === me })))
      .sort((a, b) => b.item.createdAt.localeCompare(a.item.createdAt))
      .slice(0, SHELF_MAX)
      .map(({ item, author, mine }) =>
        card(
          ctx,
          item.monster,
          mine ? t('Shared by you') : t('Shared by {name}', { name: author }),
          'shelf-card',
          mine
            ? null
            : h(
                'div',
                { class: 'card-actions' },
                button(t('Fight!'), {
                  small: true,
                  variant: 'primary',
                  color: 'pink',
                  sfx: app,
                  onClick: () => {
                    if (!app.store.hero) {
                      app.toast(t('Draw a hero first — monsters need someone to fight!'), 'info');
                      ctx.goTab('newhero');
                      return;
                    }
                    const id = tapeIn(ctx, item, author);
                    if (id) void app.scenes.go('battle', { mode: 'pve', monsterId: id }, 'page');
                  },
                }),
              ),
        ),
      );

    setChildren(
      box,
      h(
        'div',
        { class: 'class-top' },
        stickyNote(
          'yellow',
          h('p', { class: 'class-name' }, h('b', null, cls.name)),
          h('p', null, t('Class code — tell your classmates:')),
          h('p', { class: 'share-code class-code' }, formatClassCode(cls.code)),
          h(
            'div',
            { class: 'row-actions' },
            button(t('Copy code'), {
              small: true,
              variant: 'ghost',
              sfx: app,
              onClick: () =>
                void navigator.clipboard?.writeText(formatClassCode(cls.code)).then(
                  () => app.toast(t('Class code copied!'), 'success'),
                  () => app.toast(t('Could not copy. Write it down instead!'), 'info'),
                ),
            }),
            cls.mine
              ? button(t('New name'), {
                  small: true,
                  color: 'green',
                  title: t('The game picks another friendly name'),
                  sfx: app,
                  onClick: () => void act(async () => t('Your class is now called {name}.', { name: (await app.api.renameClass(language())).name })),
                })
              : null,
          ),
          h('p', { class: 'hand-hint' }, t('{n} / {max} classmates · run by {name}', { n: cls.members.length, max: cls.maxMembers, name: owner?.name ?? '?' })),
        ),
        goalNote(ctx, cls, () => void load()),
      ),
      h('h3', { class: 'section-title' }, t('Classmates ({n})', { n: cls.members.length })),
      h('ul', { class: 'friend-list class-members' }, members),
      h('h3', { class: 'section-title' }, t('Class Monster Shelf')),
      shelf.length ? h('div', { class: 'card-grid' }, shelf) : hint(t('Nobody shared a monster yet. Share one from Collection → My Doodles!')),
      h('p', { class: 'hand-hint' }, t('Only classmates see this shelf.')),
      h(
        'div',
        { class: 'row-actions' },
        button(t('Leave the class'), {
          small: true,
          variant: 'danger',
          sfx: app,
          onClick: () =>
            void confirmBox(
              t('Leave {name}?', { name: cls.name }),
              cls.mine && cls.members.length > 1 ? t('The classmate who joined first will run the class.') : cls.members.length === 1 ? t('You are the last one here, so the class closes.') : t('You can join again with the class code.'),
              t('Leave'),
            ).then((yes) => {
              if (yes)
                void act(async () => {
                  await app.api.leaveClass();
                  keepClass(app, null);
                  return t('You left {name}.', { name: cls.name });
                });
            }),
        }),
      ),
    );
  };

  const load = async (): Promise<void> => {
    if (!(await ensureOnline(app, false))) {
      setChildren(box, hint(t('Battle server offline — the class is unavailable right now.')));
      return;
    }
    void publishNotebookCard(app);
    try {
      const [cls, friends] = await Promise.all([app.api.myClass(), app.api.friends().catch(() => null)]);
      keepClass(app, cls);
      if (!alive) return;
      if (cls) render(cls, friends);
      else renderNone();
    } catch (err) {
      if (alive) setChildren(box, hint(errorText(err)));
    }
  };
  void load();
  return page;
}

/** The "My Class" note on the Friends page: a peek at the class and the way in. */
export function classPeek(ctx: NotebookCtx): HTMLElement {
  const { app } = ctx;
  const note = stickyNote('green', h('p', null, h('b', null, t('My Class'))), hint(t('Opening the class register…')));
  note.classList.add('class-peek');
  let alive = true;
  ctx.onCleanup(() => (alive = false));
  const open = button(t('Open the class board'), { small: true, color: 'blue', sfx: app, onClick: () => ctx.goTab('class') });
  app.api
    .myClass()
    .then((cls) => {
      keepClass(app, cls);
      if (!alive) return;
      setChildren(
        note,
        h('p', null, h('b', null, t('My Class'))),
        cls
          ? h(
              'p',
              null,
              h('b', null, cls.name),
              ` · ${tn(cls.members.length, '{n} classmate', '{n} classmates')} · ${t('goal {progress} / {target}', { progress: cls.goal.progress, target: cls.goal.target })}`,
              classPrizeReady(app.store.state, cls.goal) ? h('span', { class: 'stamp pink' }, t('PRIZE!')) : null,
            )
          : h('p', null, t('Join your class with a class code, or start one.')),
        cls ? open : button(t('Join or start a class'), { small: true, color: 'yellow', sfx: app, onClick: () => ctx.goTab('class') }),
      );
    })
    .catch(() => alive && setChildren(note, h('p', null, h('b', null, t('My Class'))), hint(t('Battle server offline — the class is unavailable right now.'))));
  return note;
}
